/**
 * IMC et catégorie OMS.
 *
 * L'IMC n'est jamais stocké : il se recalcule à chaque affichage depuis le poids et la
 * taille du profil. Comme la masse grasse estimée (voir `bodyComposition.ts`), c'est une
 * valeur dérivée — la stocker créerait une deuxième vérité qui dérive dès qu'on pèse.
 *
 * Les bornes sont celles de l'OMS pour l'adulte. Elles ne valent pas pour l'enfant ni
 * l'adolescent, mais le wizard n'ouvre pas l'inscription aux mineurs.
 */

export type BmiCategoryId = "underweight" | "normal" | "overweight" | "obese"

export interface BmiCategory {
  id: BmiCategoryId
  /** Libellé court, affiché sous la tuile. */
  label: string
  color: string
}

const CATEGORIES: Record<BmiCategoryId, BmiCategory> = {
  underweight: { id: "underweight", label: "Insuffisance pondérale", color: "#67e8f9" },
  normal:      { id: "normal",      label: "Corpulence normale",     color: "#4ade80" },
  overweight:  { id: "overweight",  label: "Surpoids",               color: "#fbbf24" },
  obese:       { id: "obese",       label: "Obésité",                color: "#f97316" },
}

/** IMC = poids (kg) ÷ taille (m)². `null` si l'une des deux données manque. */
export function computeBmi(weightKg: number | null | undefined, heightCm: number | null | undefined): number | null {
  if (!weightKg || !heightCm || weightKg <= 0 || heightCm <= 0) return null
  const m = heightCm / 100
  const bmi = weightKg / (m * m)
  return Number.isFinite(bmi) ? bmi : null
}

/**
 * Catégorie OMS : < 18,5 insuffisance · 18,5–24,9 normale · 25–29,9 surpoids · ≥ 30 obésité.
 * Les bornes hautes des intervalles sont ouvertes (24,95 est encore « normale », comme
 * l'arrondi affiché de 25,0 le laisserait croire — d'où le seuil strict à 25).
 */
export function getBmiCategory(bmi: number | null): BmiCategory | null {
  if (bmi == null) return null
  if (bmi < 18.5) return CATEGORIES.underweight
  if (bmi < 25) return CATEGORIES.normal
  if (bmi < 30) return CATEGORIES.overweight
  return CATEGORIES.obese
}
